import type { ReactNode } from 'react'

import { Typography } from '@gto/ui/typography'

import { AnnouncementBody } from './announcement-body'
import { AnnouncementTitle } from './announcement-title'

/** Minimum height of the strip, in px — also what the app offsets its chrome by. */
export const STRIP_BANNER_MIN_HEIGHT = 48

export type StripBannerBarProps = {
  title: string
  /** Substring of `title` rendered in the accent colour; empty string = no accent. */
  titleHighlight: string
  body: string
  /** The CTA button, when the announcement has one. */
  action?: ReactNode
  /** The close control, when the announcement is dismissible. */
  dismissControl?: ReactNode
}

/**
 * The full-width strip pinned above the app chrome, exactly as customers see it.
 *
 * The body is rendered `plain` whatever the announcement declares: this surface
 * is a single-line bar, and a chevron list inside it breaks the row. The app
 * reports a multi-line body as a content problem rather than trying to lay it
 * out — see `remote-strip-banner.tsx`.
 *
 * Presentation only — the enter/exit animation, dismissal persistence and
 * analytics stay in the app that owns them.
 */
export const StripBannerBar = ({
  title,
  titleHighlight,
  body,
  action,
  dismissControl,
}: StripBannerBarProps) => (
  <div
    className="relative flex items-center justify-center gap-8 bg-primary-main/10 p-3 md:flex-col md:gap-2"
    style={{ minHeight: STRIP_BANNER_MIN_HEIGHT }}
  >
    <Typography variant="body3">
      {/* The trailing space is what separates title from body on one line. */}
      <AnnouncementTitle
        highlight={titleHighlight}
        title={`${title} `}
      />
      <span className="text-primary-main">
        <AnnouncementBody
          body={body}
          bodyStyle="plain"
        />
      </span>
    </Typography>

    {action}
    {dismissControl}
  </div>
)
